<template>
	<DialogPanel class="template-form-panel"
		:scroll-body="true"
		@close="$emit( `close` )"
	>
		<template #title-text>{{ translate( "title" ) }}</template>

		<template #body>
			<div class="form-fields">
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFormFields.name"
						:focus="isFocused( `name` )"
						:hint="translate( `nameFieldHint` )"
						:is-valid="!errors.name"
						:label="translate( `nameFieldLabel` )"
						:placeholder="translate( `nameFieldPlaceholder` )"
						:selection="focusedSelectionFor( `name` )"
						:validation-error="errorFor( `name` )"
						:value="name"
						@click="onNameFocus"
						@input="onNameInput"
						name="name"
					/>
				</div>
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFormFields.description"
						:focus="isFocused( `description` )"
						:hint="translate( `descriptionFieldHint` )"
						:is-valid="!errors.description"
						:placeholder="translate( `descriptionFieldPlaceholder` )"
						:selection="focusedSelectionFor( `description` )"
						:validation-error="errorFor( `description` )"
						:value="description"
						@click="onDescriptionFocus"
						@input="onDescriptionInput"
						name="description"
					>
						<template #label>
							<label>{{ translate( `descriptionFieldLabel` ) }}</label>
						</template>
						<template #label-right>
							<label class="optional">{{ translate( `descriptionFieldOptionalLabel` ) }}</label>
						</template>
					</LabeledInputField>
				</div>
				<div>
					<LabeledTeamDropDown
						:disabled="disabled || disabledFormFields.category"
						:is-valid="!errors.category"
						:options="categoryOptions"
						:validation-error="errorFor( `category` )"
						:value="category"
						:hint="translate( `categoryFieldHint` )"
						:label="translate( `categoryFieldLabel` )"
						name="category"
						@change="onCategoryChanged"
					/>
				</div>
			</div>
			<div class="template-content">
				<div class="disabled-content"></div>
				<EditorContent :editor="editor" />
			</div>
		</template>

		<template #footer>
			<TeamingButton
				size="SMALL"
				@click="$emit( `save` )"
			>
				{{ translate( "saveButton" ) }}
			</TeamingButton>
			<TeamingButton
				size="SMALL"
				type="TERTIARY"
				@click="$emit( `close` )"
			>
				{{ translate( "cancelButton" ) }}
			</TeamingButton>
		</template>
	</DialogPanel>
</template>

<script>
/* eslint-disable vue/no-unused-properties */
import { Editor, EditorContent } from "@tiptap/vue-3";
import TeamingCreatorAttribute from "@teaming/tiptap-creator-attribute";
import TeamingTiptapTopic from "@teaming/tiptap-topic";
import TeamingTiptapCommitment from "@teaming/tiptap-commitment";
import TeamingTiptapDecision from "@teaming/tiptap-decision";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TiptapSlashCommands from "@teaming/tiptap-slash-commands";
import TeamingCreatedOnAttribute from "@teaming/tiptap-created-on-attribute";
import UuidExtension from "@teaming/tiptap-uuid-attribute";

import {
	FormMixin,
	LabeledInputField,
	LabeledTeamDropDown,
	DialogPanel,
	TeamingButton
} from "@teaming/common-ui";

const staticExtensions = [
	StarterKit.configure( { history: false, } ),
	Image.configure( {
		HTMLAttributes: {
			class: "teaming-text-editor__image",
		},
		inline: true,
	} ),
	Link,
	Underline,
	TiptapSlashCommands,
	TeamingCreatedOnAttribute,
	UuidExtension,
];

export default {
	name: "TemplateFormPanel",
	mixins: [ FormMixin, ],
	emits: [ "close", "save", ],
	components: {
		DialogPanel,
		LabeledInputField,
		LabeledTeamDropDown,
		TeamingButton,
		EditorContent,
	},
	provide() {
		return {
			context: this.context,
		};
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		context: {
			type: Object,
			required: true,
		},
		name: {
			type: String,
			required: true,
		},
		description: {
			type: String,
			required: true,
		},
		category: {
			type: String,
			required: true,
		},
		categoryOptions: {
			type: Array,
			required: true,
		},
		content: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			editor: null,
			formNameEvent: null,
			formDescriptionEvent: null,
		};
	},
	computed: {
		form() {
			const {
				name,
				description,
				category,
				content,
			} = this;

			return {
				name,
				description,
				category,
				content,
			};
		},
		disabledFormFields() {
			return {
				name: false,
				description: false,
				category: false,
				content: false,
				/** see FormMixin **/
				...this.disabledFields,
			};
		},
	},
	methods: {
		translate( key, args = {} ) {
			const { name, } = this.$options;
			return this.$t( `common-team.components.${ name }.${ key }`, args );
		},
		onNameInput( e ) {
			clearTimeout( this.formNameEvent );
			this.formNameEvent = setTimeout( () => {
				const name = e.target.value;
				this.raiseFormChange( {
					name,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "name", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 150 );
		},
		onNameFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "name", selectionStart );
		},
		onDescriptionInput( e ) {
			clearTimeout( this.formNameEvent );
			this.formDescriptionEvent = setTimeout( () => {
				const description = e.target.value;
				this.raiseFormChange( {
					description,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "description", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 150 );
		},
		onDescriptionFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "description", selectionStart );
		},
		onCategoryChanged( option ) {
			const { value: category, } = option;
			this.raiseFormChange( {
				category,
			} );
			this.raiseFocusChange( "category" );
		},
	},
	async mounted() {
		const { content, } = this;
		const noop = () => {};
		this.editor = new Editor( {
			extensions: [
				...staticExtensions,
				TeamingCreatorAttribute.configure( { getPersonId: noop, } ),
				TeamingTiptapTopic.configure( { userName: "", onTeamingExtensionCallback: noop, } ),
				TeamingTiptapCommitment.configure( { onTeamingExtensionCallback: noop, } ),
				TeamingTiptapDecision.configure( { onTeamingExtensionCallback: noop, } ),
			],
		} );
		this.editor.setEditable( false );
		await this.$nextTick();
		this.editor.commands.setContent( content );
	},
	async updated() {
		const { content, } = this;
		await this.$nextTick();
		this.editor.commands.setContent( content );
	},
	beforeUnmount() {
		this.editor = null;
	},
};
</script>

<style lang="scss" scoped>
$optionalColor: $color3-400;

.template-form-panel .form-fields {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	// ensure the fields are "above" the template
	// content, or else they may fall beneath the
	// transparent "disabled-content" div, and the
	// user won't be able to interact with the fields
	position: relative;
	z-index: 20;
}

.template-form-panel .optional {
	@include font-and-line-for(-1);
	font-weight: 600;
	color: $optionalColor;
}

.template-form-panel .template-content {
	position: relative;
}

.template-form-panel .disabled-content {
	z-index: 10;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	width: 100%;
}
</style>

<style lang="scss">
.template-form-panel .body {
	margin-top: 1rem;
	padding: 1rem;
}
</style>
